/**
 * Kai scheduled-reports-snapshot artifact types — mirror
 * `tideway-kai/src/orchestrator/artifacts/scheduled_reports_snapshot.rs`.
 */

export interface KaiScheduledReportRow {
  id: string;
  name: string;
  report_type: string;
  report_type_display: string;
  schedule_summary: string;
  next_delivery_utc?: string | null;
  created_by: string;
  subscribed: boolean;
  paused: boolean;
  is_mine: boolean;
  can_subscribe: boolean;
  can_view_on_bookings: boolean;
  can_manage: boolean;
}

export interface KaiScheduledReportsSnapshot {
  title?: string | null;
  context: "all" | "bookings" | string;
  digest?: Record<string, unknown> | null;
  rows: KaiScheduledReportRow[];
  footnotes?: string[];
  open_in_reports_url: string;
}

export interface KaiScheduledReportsSnapshotRefSpec {
  id: string;
  artifact?: unknown;
}

const VALID_CONTEXTS = new Set(["all", "bookings"]);

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function parseRow(row: unknown): KaiScheduledReportRow | null {
  if (!isRecord(row)) return null;
  const id = typeof row.id === "string" ? row.id.trim() : "";
  if (!id) return null;
  return {
    id,
    name: typeof row.name === "string" ? row.name : "",
    report_type: typeof row.report_type === "string" ? row.report_type : "",
    report_type_display:
      typeof row.report_type_display === "string" ? row.report_type_display : "",
    schedule_summary:
      typeof row.schedule_summary === "string" ? row.schedule_summary : "",
    next_delivery_utc:
      typeof row.next_delivery_utc === "string" ? row.next_delivery_utc : null,
    created_by: typeof row.created_by === "string" ? row.created_by : "",
    subscribed: row.subscribed === true,
    paused: row.paused === true,
    is_mine: row.is_mine === true,
    can_subscribe: row.can_subscribe === true,
    can_view_on_bookings: row.can_view_on_bookings === true,
    can_manage: row.can_manage === true,
  };
}

/** Parse + validate a `scheduled_reports_snapshot` fenced body or payload. */
export function parseScheduledReportsSnapshotSpec(
  body: string,
): KaiScheduledReportsSnapshot | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!isRecord(parsed)) return null;

    const context =
      typeof parsed.context === "string" ? parsed.context.trim() : "";
    if (!VALID_CONTEXTS.has(context)) return null;

    const openUrl =
      typeof parsed.open_in_reports_url === "string"
        ? parsed.open_in_reports_url.trim()
        : "";
    if (!openUrl) return null;

    const rowsRaw = Array.isArray(parsed.rows) ? parsed.rows : [];
    const rows = rowsRaw
      .map(parseRow)
      .filter((r): r is KaiScheduledReportRow => r != null);

    const footnotes = Array.isArray(parsed.footnotes)
      ? parsed.footnotes.filter(
          (f): f is string => typeof f === "string" && f.trim() !== "",
        )
      : [];

    return {
      title: typeof parsed.title === "string" ? parsed.title : null,
      context,
      digest: isRecord(parsed.digest) ? parsed.digest : null,
      rows,
      footnotes,
      open_in_reports_url: openUrl,
    };
  } catch {
    return null;
  }
}

/** Parse a compact `scheduled_reports_snapshot_ref` fence body `{ "id": "..." }`. */
export function parseScheduledReportsSnapshotRefSpec(
  body: string,
): KaiScheduledReportsSnapshotRefSpec | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!isRecord(parsed)) return null;
    const id = typeof parsed.id === "string" ? parsed.id.trim() : "";
    if (!id) return null;
    return { id, artifact: parsed.artifact };
  } catch {
    return null;
  }
}
